import { createSlice } from "@reduxjs/toolkit";
import {
  MetricLap,
  PerformanceMetrics,
  PerformanceMetricsFilters,
} from "../types/performanceMetrics";

const defaultFilters: PerformanceMetricsFilters = {
  lapTimeMin: -Infinity,
  lapTimeMax: Infinity,
  disabledPoints: [],
  disabledDrivers: [],
  disabledSessions: [],
};

const initialState = {
  livePerformanceMetricsData: {
    MetricSpec: {},
    MetricLaps: [],
  } as PerformanceMetrics,
  loadedPerformanceMetricsData: {
    MetricSpec: {},
    MetricLaps: [],
  } as PerformanceMetrics,
  filteredPerformanceMetricsData: {
    MetricSpec: {},
    MetricLaps: [],
  } as PerformanceMetrics,
  filters: { ...defaultFilters } as PerformanceMetricsFilters,
  // Spinner over just the graphs (e.g. while filters are being re-applied)
  loadingGraphs: false,
  isViewingLoadedData: false,
  // Spinner over the whole page content (e.g. while switching/loading a data source)
  loadingPage: false,
};

const sessionOrder = [
  "SD",
  "FP1",
  "FP2",
  "QA1",
  "QB1",
  "Race1",
  "FP3",
  "QA2",
  "QB2",
  "Race2",
];

function mergePerformanceMetricsLaps(
  existing: MetricLap[],
  incoming: MetricLap[],
): MetricLap[] {
  if (existing.length === 0) {
    return incoming;
  }
  // Start at index 1 to ALWAYS skip first run break for continuity.
  for (
    let incomingIndex = 1;
    incomingIndex < incoming.length;
    ++incomingIndex
  ) {
    const incomingLap = incoming[incomingIndex];

    // Check if incoming lap is a run break for a run that already has a run break
    if (incomingLap?.name?.[0] === "RunBreak") {
      const existingRunBreakIndex = existing.findIndex(
        (lap) =>
          lap.name?.[0] === "RunBreak" &&
          lap.description?.[0] === incomingLap.description?.[0],
      );
      // If so skip this lap
      if (existingRunBreakIndex !== -1) {
        continue;
      }
    }

    // Check if lap exists already, if so merge it
    const existingLapIndex = existing.findIndex(
      (lap) =>
        lap.name?.[0] === incomingLap?.name?.[0] &&
        lap.description?.[0] === incomingLap?.description?.[0] &&
        lap.lapname === incomingLap.lapname,
    );
    if (existingLapIndex !== -1) {
      const existingLap = existing[existingLapIndex];
      // Don't replace existing RunBreaks
      if (existingLap?.name?.[0] !== "RunBreak") {
        // Merge the lap - overwrite all properties except metrics
        const mergedLap = { ...existingLap, ...incomingLap };

        // Deep merge metrics: only overwrite individual metrics if incoming has non-null value
        if (existingLap.metrics && incomingLap.metrics) {
          const mergedMetrics = { ...existingLap.metrics };
          for (const metricKey of Object.keys(incomingLap.metrics)) {
            const incomingMetricValue =
              incomingLap.metrics[
                metricKey as keyof typeof incomingLap.metrics
              ];
            if (
              typeof incomingMetricValue === "number" &&
              !isNaN(incomingMetricValue)
            ) {
              (mergedMetrics as Record<string, number | null>)[metricKey] =
                incomingMetricValue;
            }
          }
          mergedLap.metrics = mergedMetrics;
        }
        existing[existingLapIndex] = mergedLap;
      }
      continue;
    }

    // Else find the most recent lap for the same driver in the existing data, if exists add to right of it
    let foundDriver = false;
    for (
      let existingIndex = existing.length - 1;
      existingIndex >= 0;
      --existingIndex
    ) {
      const existingLap = existing[existingIndex];
      if (existingLap.driver === incomingLap.driver) {
        existing.splice(existingIndex + 1, 0, incomingLap);
        foundDriver = true;
        break;
      }
    }

    // If driver not found, add the lap to the end of the list
    if (!foundDriver) {
      existing.push(incomingLap);
    }
  }

  // Once all laps have been added, sort each driver's runs by session order and R number
  if (existing.length > 1) {
    const firstPoint = existing[0];
    const rest = existing.slice(1);

    // Group by driver, preserving order of first appearance
    const driverOrder: string[] = [];
    const byDriver: Record<string, MetricLap[]> = {};
    for (const lap of rest) {
      const driver = lap.driver ?? "";
      if (!byDriver[driver]) {
        driverOrder.push(driver);
        byDriver[driver] = [];
      }
      byDriver[driver].push(lap);
    }

    // Sort each driver's laps by session then run number
    for (const driver of driverOrder) {
      const driverLaps = byDriver[driver];

      // Track insertion order of unknown session names
      const unknownSessionOrder: string[] = [];
      for (const lap of driverLaps) {
        // Extract session (second word in description)
        const session = lap.description?.[0]?.split(" ")?.[1] ?? "";
        if (
          sessionOrder.indexOf(session) === -1 &&
          !unknownSessionOrder.includes(session)
        ) {
          unknownSessionOrder.push(session);
        }
      }

      driverLaps.sort((a, b) => {
        // Extract session (second word in description)
        const sessionA = a.description?.[0]?.split(" ")?.[1] ?? "";
        const sessionB = b.description?.[0]?.split(" ")?.[1] ?? "";
        const sessionIdxA = sessionOrder.indexOf(sessionA);
        const sessionIdxB = sessionOrder.indexOf(sessionB);

        // Get effective index: known sessions use sessionOrder, unknown use their insertion order + offset
        const effectiveIdxA =
          sessionIdxA === -1
            ? sessionOrder.length + unknownSessionOrder.indexOf(sessionA)
            : sessionIdxA;
        const effectiveIdxB =
          sessionIdxB === -1
            ? sessionOrder.length + unknownSessionOrder.indexOf(sessionB)
            : sessionIdxB;

        if (effectiveIdxA !== effectiveIdxB) {
          return effectiveIdxA - effectiveIdxB;
        }
        // Same session - sort by run number (R1, R2, etc.)
        const runA = a.description?.[0]?.match(/R(\d+)/)?.[1] ?? "0";
        const runB = b.description?.[0]?.match(/R(\d+)/)?.[1] ?? "0";
        return parseInt(runA, 10) - parseInt(runB, 10);
      });
    }

    // Reconstruct: first point + each driver's sorted laps in order
    existing.length = 0;
    existing.push(firstPoint);
    for (const driver of driverOrder) {
      existing.push(...byDriver[driver]);
    }
  }

  return existing;
}

function applyFilters(
  data: PerformanceMetrics,
  filters: typeof initialState.filters,
): PerformanceMetrics {
  const filteredLaps = [];
  for (const lap of data.MetricLaps) {
    const isRunBreak = lap.name?.[0] === "RunBreak";
    const inLapTimeRange =
      lap.laptime !== null &&
      lap.laptime >= filters.lapTimeMin &&
      lap.laptime <= filters.lapTimeMax;
    const isDriverDisabled =
      lap.driver !== null && filters.disabledDrivers.includes(lap.driver);
    const session = lap.description?.[0]?.split(" ")[1];
    const isSessionDisabled =
      session !== undefined && filters.disabledSessions.includes(session);
    if (
      (inLapTimeRange || isRunBreak) &&
      !isDriverDisabled &&
      !isSessionDisabled
    ) {
      filteredLaps.push({
        ...lap,
        metrics: lap.metrics ? { ...lap.metrics } : lap.metrics,
      });
    }
  }
  for (const disabledPoint of filters.disabledPoints) {
    for (const lap of filteredLaps) {
      if (
        lap.description &&
        lap.description[0] === disabledPoint.description[0] &&
        lap.lapname === disabledPoint.lapname &&
        lap.metrics
      ) {
        if (disabledPoint.metric === "Lap Time") {
          lap.laptime = null;
        } else {
          lap.metrics[disabledPoint.metric] = null;
        }
      }
    }
  }
  // If first entry isn't a RunBreak after filtering, make one by duplicating the first entry and setting its name to RunBreak
  if (filteredLaps.length > 0 && filteredLaps[0].name?.[0] !== "RunBreak") {
    const firstLap = filteredLaps[0];
    const runBreakLap = {
      ...firstLap,
      name: ["RunBreak"],
      laptime: null,
      metrics: firstLap.metrics ? { ...firstLap.metrics } : firstLap.metrics,
    };
    filteredLaps.unshift(runBreakLap);
  }
  return {
    MetricSpec: data.MetricSpec,
    MetricLaps: filteredLaps,
  };
}

export const performanceMetricsSlice = createSlice({
  name: "performanceMetrics",
  initialState,
  reducers: {
    setPerformanceMetricsData: (state, action) => {
      state.livePerformanceMetricsData = action.payload;
      if (!state.isViewingLoadedData) {
        state.filteredPerformanceMetricsData = action.payload;
      }
    },
    mergePerformanceMetricsUpdate: (state, action) => {
      const incoming = action.payload as PerformanceMetrics;
      const mergedLaps = mergePerformanceMetricsLaps(
        [...state.livePerformanceMetricsData.MetricLaps],
        incoming.MetricLaps,
      );
      state.livePerformanceMetricsData = {
        MetricSpec: incoming.MetricSpec,
        MetricLaps: mergedLaps,
      };
      if (!state.isViewingLoadedData) {
        state.filteredPerformanceMetricsData = applyFilters(
          state.livePerformanceMetricsData,
          state.filters,
        );
      }
    },
    setLoadedPerformanceMetricsData: (state, action) => {
      state.loadedPerformanceMetricsData = action.payload;
      state.isViewingLoadedData = true;
      state.filters = { ...defaultFilters };
      state.filteredPerformanceMetricsData = state.loadedPerformanceMetricsData;
    },
    viewLivePerformanceMetricsData: (state) => {
      state.isViewingLoadedData = false;
      state.filters = { ...defaultFilters };
      state.filteredPerformanceMetricsData = state.livePerformanceMetricsData;
    },
    viewLoadedPerformanceMetricsData: (state) => {
      state.isViewingLoadedData = true;
      state.filters = { ...defaultFilters };
      state.filteredPerformanceMetricsData = state.loadedPerformanceMetricsData;
    },
    setLapTimeFilter: (state, action) => {
      state.filters.lapTimeMin = action.payload.min;
      state.filters.lapTimeMax = action.payload.max;
      state.filteredPerformanceMetricsData = applyFilters(
        state.isViewingLoadedData
          ? state.loadedPerformanceMetricsData
          : state.livePerformanceMetricsData,
        state.filters,
      );
    },
    disablePoint: (state, action) => {
      const { description, lapname, metric } = action.payload;
      if (
        !state.filters.disabledPoints.some(
          (point) =>
            point.description === description &&
            point.lapname === lapname &&
            point.metric === metric,
        )
      ) {
        state.filters.disabledPoints.push({ description, lapname, metric });
      }
      const lap = state.filteredPerformanceMetricsData.MetricLaps.find(
        (l) =>
          l.description &&
          l.description[0] === description[0] &&
          l.lapname === lapname,
      );
      if (lap) {
        if (metric === "Lap Time") {
          lap.laptime = null;
        } else if (lap.metrics) {
          lap.metrics[metric] = null;
        }
      }
    },
    enableDriver: (state, action) => {
      const driver = action.payload;
      state.filters.disabledDrivers = state.filters.disabledDrivers.filter(
        (d) => d !== driver,
      );
      state.filteredPerformanceMetricsData = applyFilters(
        state.isViewingLoadedData
          ? state.loadedPerformanceMetricsData
          : state.livePerformanceMetricsData,
        state.filters,
      );
    },
    disableDriver: (state, action) => {
      const driver = action.payload;
      if (!state.filters.disabledDrivers.includes(driver)) {
        state.filters.disabledDrivers.push(driver);
      }
      state.filteredPerformanceMetricsData = applyFilters(
        state.isViewingLoadedData
          ? state.loadedPerformanceMetricsData
          : state.livePerformanceMetricsData,
        state.filters,
      );
    },
    enableSession: (state, action) => {
      const session = action.payload;
      state.filters.disabledSessions = state.filters.disabledSessions.filter(
        (s) => s !== session,
      );
      state.filteredPerformanceMetricsData = applyFilters(
        state.isViewingLoadedData
          ? state.loadedPerformanceMetricsData
          : state.livePerformanceMetricsData,
        state.filters,
      );
    },
    disableSession: (state, action) => {
      const session = action.payload;
      if (!state.filters.disabledSessions.includes(session)) {
        state.filters.disabledSessions.push(session);
      }
      state.filteredPerformanceMetricsData = applyFilters(
        state.isViewingLoadedData
          ? state.loadedPerformanceMetricsData
          : state.livePerformanceMetricsData,
        state.filters,
      );
    },
    resetFilters: (state) => {
      state.filters = { ...defaultFilters };
      state.filteredPerformanceMetricsData = state.isViewingLoadedData
        ? state.loadedPerformanceMetricsData
        : state.livePerformanceMetricsData;
    },
    setLoadingGraphs: (state, action) => {
      state.loadingGraphs = action.payload;
    },
    setLoadingPage: (state, action) => {
      state.loadingPage = action.payload;
    },
  },
});

export const {
  setPerformanceMetricsData,
  mergePerformanceMetricsUpdate,
  setLoadedPerformanceMetricsData,
  viewLivePerformanceMetricsData,
  viewLoadedPerformanceMetricsData,
  setLapTimeFilter,
  disablePoint,
  enableDriver,
  disableDriver,
  enableSession,
  disableSession,
  resetFilters,
  setLoadingGraphs,
  setLoadingPage,
} = performanceMetricsSlice.actions;
export default performanceMetricsSlice.reducer;
